import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Chip,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import styled from 'styled-components';
import {
  CreateQuestionnaireTemplateInput,
  QuestionnaireType
} from '../../../types/questionnaireTemplates.types';
import {
  ButtonContainer,
  StyledForm
} from '../../ImpactClaims/ActiveImpactClaim/ImpactClaimForm/styles';
import { useQuestionnaireTemplates } from '../hooks/useQuestionnaireTemplates';

const FormFieldsContainer = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: ${({ theme }) => theme.spacing(3)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const JsonTextField = styled(TextField)`
  .MuiInputBase-input {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
    font-size: 13px;
    line-height: 1.5;
  }
  .MuiInputBase-root {
    max-height: 400px;
    overflow-y: auto;
  }
`;

const QuestionTypeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const QuestionTypeChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const QUESTIONNAIRE_TYPE_OPTIONS: {
  label: string;
  value: QuestionnaireType;
}[] = [{ label: 'Self Assessment', value: 'SELF_ASSESSMENT' }];

const SECTION_TEMPLATE = {
  title: 'Section Title',
  description: 'Section description',
  questions: []
};

const QUESTION_TYPE_TEMPLATES: { label: string; template: object }[] = [
  {
    label: 'No Answer',
    template: {
      text: 'The following questions assess your deforestation risk management practices.',
      question_type: 'no_answer',
      is_required: false,
      options: null
    }
  },
  {
    label: 'Short Text',
    template: {
      text: "What is your internal compliance officer's name?",
      description:
        'Provide the full name of the person responsible for compliance',
      question_type: 'short_free_text',
      is_required: true,
      options: null
    }
  },
  {
    label: 'Long Text',
    template: {
      text: 'Describe your due diligence process for verifying plot-level geolocation data',
      question_type: 'long_free_text',
      is_required: true,
      options: null
    }
  },
  {
    label: 'Single Select',
    template: {
      text: 'Does your company have a documented compliance policy?',
      question_type: 'single_select',
      is_required: true,
      options: [
        {
          text: 'Yes, fully documented and implemented',
          value: 0.0,
          is_comment_required: false
        },
        {
          text: 'Yes, documented but not fully implemented',
          value: 0.5,
          is_comment_required: true
        },
        {
          text: 'No, policy is in development',
          value: 0.8,
          is_comment_required: true
        },
        { text: 'No policy exists', value: 1.0, is_comment_required: true }
      ]
    }
  },
  {
    label: 'Multi Select',
    template: {
      text: 'Which sustainability certifications does your company hold?',
      question_type: 'multi_select',
      is_required: false,
      options: [
        { text: 'Rainforest Alliance', value: 0.0, is_comment_required: false },
        { text: 'Fair Trade', value: 0.0, is_comment_required: false },
        { text: 'Organic (EU/USDA)', value: 0.0, is_comment_required: false },
        { text: 'UTZ Certified', value: 0.0, is_comment_required: false },
        { text: 'None of the above', value: 0.6, is_comment_required: false }
      ]
    }
  },
  {
    label: 'Single Dropdown',
    template: {
      text: 'What is the primary commodity sourced from this supply chain?',
      question_type: 'single_select_drop_down',
      is_required: true,
      options: [
        { text: 'Cattle', value: 0.7, is_comment_required: false },
        { text: 'Cocoa', value: 0.4, is_comment_required: false },
        { text: 'Coffee', value: 0.3, is_comment_required: false },
        { text: 'Oil Palm', value: 0.6, is_comment_required: false },
        { text: 'Rubber', value: 0.5, is_comment_required: false },
        { text: 'Soy', value: 0.5, is_comment_required: false },
        { text: 'Wood', value: 0.8, is_comment_required: false }
      ]
    }
  },
  {
    label: 'Multi Dropdown',
    template: {
      text: 'In which countries do you source this commodity?',
      question_type: 'multi_select_drop_down',
      is_required: true,
      options: [
        { text: 'Brazil', value: 0.6, is_comment_required: false },
        { text: 'Indonesia', value: 0.7, is_comment_required: false },
        { text: 'Ivory Coast', value: 0.5, is_comment_required: false },
        { text: 'Colombia', value: 0.4, is_comment_required: false },
        { text: 'Ghana', value: 0.4, is_comment_required: false }
      ]
    }
  },
  {
    label: 'Documents',
    template: {
      text: "Upload your company's compliance policy document",
      description: 'Accepted formats: PDF, DOCX. Maximum file size: 10MB',
      question_type: 'documents',
      is_required: true,
      options: null
    }
  }
];

export const QuestionnaireTemplateForm: React.FC = () => {
  const { handleCreate, loading } = useQuestionnaireTemplates();
  const navigate = useNavigate();
  const {
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    reset,
    setValue,
    trigger
  } = useForm<CreateQuestionnaireTemplateInput>({
    mode: 'onTouched'
  });

  const templateDataRegistration = register('templateData', {
    required: 'Template data is required',
    validate: value => {
      if (!value || value.trim() === '') return true;
      try {
        JSON.parse(value);
        return true;
      } catch {
        return 'Invalid JSON format';
      }
    }
  });

  const onSubmit = (data: CreateQuestionnaireTemplateInput) => {
    handleCreate(data);
  };

  const handleInsertSection = () => {
    const currentValue = getValues('templateData') || '';
    let newValue: string;

    try {
      const parsed = JSON.parse(currentValue);
      if (parsed.sections && Array.isArray(parsed.sections)) {
        parsed.sections.push({ ...SECTION_TEMPLATE });
        newValue = JSON.stringify(parsed, null, 2);
      } else {
        newValue = JSON.stringify(
          { sections: [{ ...SECTION_TEMPLATE }] },
          null,
          2
        );
      }
    } catch {
      newValue = JSON.stringify(
        { sections: [{ ...SECTION_TEMPLATE }] },
        null,
        2
      );
    }

    setValue('templateData', newValue, { shouldValidate: true });
  };

  const handleInsertQuestionType = (template: object) => {
    const currentValue = getValues('templateData') || '';
    let newValue: string;

    try {
      const parsed = JSON.parse(currentValue);
      if (
        parsed.sections &&
        Array.isArray(parsed.sections) &&
        parsed.sections.length > 0
      ) {
        // Add question to the last section
        const lastSection = parsed.sections[parsed.sections.length - 1];
        if (!lastSection.questions) {
          lastSection.questions = [];
        }
        lastSection.questions.push(template);
        newValue = JSON.stringify(parsed, null, 2);
      } else {
        // Create a new section with the question
        newValue = JSON.stringify(
          {
            sections: [
              {
                title: 'Section 1',
                description: null,
                questions: [template]
              }
            ]
          },
          null,
          2
        );
      }
    } catch {
      // Create a new section with the question
      newValue = JSON.stringify(
        {
          sections: [
            {
              title: 'Section 1',
              description: null,
              questions: [template]
            }
          ]
        },
        null,
        2
      );
    }

    setValue('templateData', newValue, { shouldValidate: true });
  };

  const handleDiscard = () => {
    navigate('/questionnaire-templates');
    reset();
  };

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <FormFieldsContainer>
        <TextField
          fullWidth
          required
          error={!!errors.title}
          helperText={errors.title?.message}
          label="Title"
          {...register('title', { required: 'Title is required' })}
        />
        <TextField
          fullWidth
          multiline
          label="Description"
          minRows={2}
          {...register('description')}
        />
        <Select
          displayEmpty
          fullWidth
          required
          defaultValue=""
          error={!!errors.questionnaireType}
          {...register('questionnaireType', { required: 'Type is required' })}
        >
          <MenuItem disabled value="">
            Select type
          </MenuItem>
          {QUESTIONNAIRE_TYPE_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
        <QuestionTypeSection>
          <Typography color="textSecondary" variant="body2">
            Build template structure:
          </Typography>
          <QuestionTypeChips>
            <Chip
              clickable
              color="secondary"
              label="+ Section"
              size="small"
              variant="filled"
              onClick={handleInsertSection}
            />
            {QUESTION_TYPE_TEMPLATES.map(({ label, template }) => (
              <Chip
                clickable
                color="primary"
                key={label}
                label={label}
                size="small"
                variant="outlined"
                onClick={() => handleInsertQuestionType(template)}
              />
            ))}
          </QuestionTypeChips>
        </QuestionTypeSection>
        <JsonTextField
          fullWidth
          multiline
          required
          error={!!errors.templateData}
          helperText={errors.templateData?.message || 'Enter valid JSON data'}
          InputLabelProps={{ shrink: true }}
          label="Template Data (JSON)"
          maxRows={20}
          minRows={10}
          placeholder='{"sections": []}'
          {...templateDataRegistration}
          onChange={e => {
            templateDataRegistration.onChange(e);
            trigger('templateData');
          }}
        />
      </FormFieldsContainer>
      <ButtonContainer>
        <Button
          color="primary"
          size="large"
          type="button"
          variant="contained"
          onClick={handleDiscard}
        >
          Discard
        </Button>
        <Button
          color="secondary"
          disabled={loading}
          size="large"
          type="submit"
          variant="contained"
        >
          Save & Create
        </Button>
      </ButtonContainer>
    </StyledForm>
  );
};
